import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Nav, Table, Spinner } from 'react-bootstrap';
import { createTransaction, createTicker, getAllAccounts, getAccount, getAccountTickers, getAggregatedTickers, getAllTransactions, getOpenLots } from '../services/api';
import { getCurrentLocalDateTime } from '../utils/dateUtils';
import { markAccountTickersRefreshRequired } from '../utils/accountTickerRefreshFlag';
import { createLogger } from '../utils/debugLogger';

// Create debug logger for this module
const logger = createLogger('transactionModal', 'Transaction creation and editing modal', false, 'INFO');

const TransactionModal = ({
  show,
  onHide,
  accountId,
  currentCash,
  availableTickers,
  setAvailableTickers,
  onTransactionComplete,
  accountTickers = [], // Add prop for current account tickers with default empty array
  initialData = null, // Add prop for initial data from table dropdown
}) => {
  // Common state
  const [transactionType, setTransactionType] = useState('BUY');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('security');
  const [showReview, setShowReview] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [priceUnavailable, setPriceUnavailable] = useState(false);
  const [sellAllClicked, setSellAllClicked] = useState(false);
  const [stopoutTriggered, setStopoutTriggered] = useState(false);

  const handleNotesPresetClick = (presetText) => {
    setNotes((previousNotes) => {
      const currentNotes = previousNotes ?? '';
      const hasContent = currentNotes.trim().length > 0;

      if (!hasContent) {
        return presetText;
      }

      const normalizedExistingNotes = currentNotes
        .split(/\r?\n/)
        .map((line) => line.trim().toLowerCase())
        .filter(Boolean);

      if (normalizedExistingNotes.includes(presetText.trim().toLowerCase())) {
        return currentNotes;
      }

      const separator = currentNotes.endsWith('\n') ? '' : '\n';

      return `${currentNotes}${separator}${presetText}`;
    });
  };
  
  // Account selection state (when accountId is null)
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountName, setAccountName] = useState('');
  
  // Security transaction state
  const [tickerSymbol, setTickerSymbol] = useState('');
  const [shares, setShares] = useState('');
  const [pricePerShare, setPricePerShare] = useState('');
  const [transactionDate, setTransactionDate] = useState(new Date().toLocaleDateString('en-CA'));

  // Cash transaction state
  const [amount, setAmount] = useState('');
  const [cashType, setCashType] = useState('deposit');
  const [cashTransactionDate, setCashTransactionDate] = useState(getCurrentLocalDateTime());

  // Initialize form with initial data if provided
  React.useEffect(() => {
    if (initialData && show) {
      setActiveTab('security');
      
      if (initialData.type === 'BUY' || initialData.type === 'SELL') {
        setTransactionType(initialData.type);
        
        if (initialData.ticker) {
          setTickerSymbol(initialData.ticker.symbol);
          // Set price per share to current price if available
          if (initialData.ticker.current_price) {
            setPricePerShare(initialData.ticker.current_price.toString());
          }
          
          // For SELL, preload existing shares
          if (initialData.type === 'SELL' && initialData.existingShares) {
            setShares(formatShares(initialData.existingShares));
          }
        }
        
        // Set shares and price from initialData if provided (for both BUY and SELL)
        if (initialData.shares) {
          setShares(initialData.shares.toString());
        }
        if (initialData.price_per_share) {
          setPricePerShare(initialData.price_per_share.toString());
        }
      }
    }
  }, [initialData, show]);

  // Open (unsold) LIFO lots for the sell preview below, fetched whenever a SELL is
  // being entered for a known ticker+account (mirrors the guard in calculateProfit()).
  const [openLots, setOpenLots] = useState([]);
  React.useEffect(() => {
    const tickerId = initialData?.ticker?.ticker_id;
    const sellAccountId = initialData?.ticker?.account_id ?? accountId;
    if (show && initialData?.type === 'SELL' && tickerId && sellAccountId) {
      getOpenLots(tickerId, sellAccountId)
        .then(response => setOpenLots(response.data?.lots || []))
        .catch(() => setOpenLots([]));
    } else {
      setOpenLots([]);
    }
  }, [show, initialData, accountId]);

  // Helper function to get the most recently used account
  const getMostRecentlyUsedAccount = async (availableAccounts) => {
    try {
      // Check localStorage cache first (cache for 5 minutes)
      const cacheKey = 'mostRecentlyUsedAccount';
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { accountId, timestamp } = JSON.parse(cached);
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
        if (timestamp > fiveMinutesAgo) {
          // Verify the cached account still exists in the available accounts
          const cachedAccount = availableAccounts.find(a => a.account_id === accountId);
          if (cachedAccount) {
            logger.debug(`Using cached most recently used account: ${cachedAccount.name}`);
            return cachedAccount;
          }
        }
      }

      // Get recent transactions to find most recently used account
      const response = await getAllTransactions(1, 10); // Get last 10 transactions
      
      if (response?.data?.transactions && response.data.transactions.length > 0) {
        // Find the first transaction with an account that exists in our available accounts
        for (const transaction of response.data.transactions) {
          const recentAccount = availableAccounts.find(a => a.account_id === transaction.account_id);
          if (recentAccount) {
            // Cache the result
            localStorage.setItem(cacheKey, JSON.stringify({
              accountId: recentAccount.account_id,
              timestamp: Date.now()
            }));
            
            logger.debug(`Most recently used account from transactions: ${recentAccount.name}`);
            return recentAccount;
          }
        }
      }
      
      return null; // No recent transactions found
    } catch (error) {
      logger.error('Error getting most recently used account:', error);
      return null;
    }
  };

  // Update the cache when a transaction is completed
  const updateMostRecentlyUsedAccountCache = (accountId) => {
    const cacheKey = 'mostRecentlyUsedAccount';
    localStorage.setItem(cacheKey, JSON.stringify({
      accountId: accountId,
      timestamp: Date.now()
    }));
  };

  // Fetch accounts when modal is shown
  useEffect(() => {
    if (show) {
      const fetchSingleAccount = async (id) => {
        try {
          if (!id) return false;
          
          // Convert to number if it's a string
          const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
          if (isNaN(numericId)) {
            logger.error('Invalid account ID format:', id);
            return false;
          }
          
          const response = await getAccount(numericId);
          if (response && response.data) {
            const account = response.data;
            const displayName = account.portfolio_name ? 
              `${account.name} - ${account.portfolio_name}` : 
              account.name;
            
            logger.debug('Account found via direct API call:', displayName);
            setAccountName(displayName);
            return true;
          }
          return false;
        } catch (err) {
          logger.error('Error fetching single account:', err);
          return false;
        }
      };
      
      const fetchAccounts = async () => {
        try {
          setLoadingAccounts(true);
          const response = await getAllAccounts();
          
          // Ensure we have valid account data
          if (response && response.data && Array.isArray(response.data)) {
            // Sort accounts by balance (highest first) as fallback
            const sortedAccounts = [...response.data].sort((a, b) => {
              const balanceA = a.balance || 0;
              const balanceB = b.balance || 0;
              return balanceB - balanceA; // Descending order (highest balance first)
            });
            
            setAccounts(sortedAccounts);
            
            if (accountId) {
              // Handle both string and number types for accountId
              const numericAccountId = typeof accountId === 'string' ? parseInt(accountId, 10) : accountId;
              
              // Find account name when accountId is provided
              const account = sortedAccounts.find(a => 
                a.account_id === numericAccountId || 
                a.account_id === accountId // Try both formats
              );
              
              if (account) {
                const displayName = account.portfolio_name ? 
                  `${account.name} - ${account.portfolio_name}` : 
                  account.name;
                logger.debug('Account found in accounts list:', displayName);
                setAccountName(displayName);
              } else {
                logger.warn('Account not found in all accounts response:', accountId);
                
                // Try to get account directly by ID if not found in the list
                const success = await fetchSingleAccount(accountId);
                
                if (!success) {
                  // Fall back to checking account tickers
                  if (accountTickers && accountTickers.length > 0) {
                    // Check if account_name is available in the tickers
                    const tickerWithAccount = accountTickers.find(t => t.account_name);
                    if (tickerWithAccount && tickerWithAccount.account_name) {
                      logger.debug('Using account name from tickers:', tickerWithAccount.account_name);
                      setAccountName(tickerWithAccount.account_name);
                    } else {
                      // Last resort: display a descriptive account identifier
                      setAccountName(`Account #${accountId}`);
                    }
                  } else {
                    // Last resort: display a descriptive account identifier
                    setAccountName(`Account #${accountId}`);
                  }
                }
              }
            } else if (sortedAccounts.length > 0) {
              // Try to get the most recently used account first
              const mostRecentAccount = await getMostRecentlyUsedAccount(sortedAccounts);
              
              if (mostRecentAccount) {
                // Select the most recently used account
                setSelectedAccount(mostRecentAccount.account_id);
                logger.debug(`Auto-selected most recently used account: ${mostRecentAccount.name} ($${mostRecentAccount.balance?.toLocaleString() || '0'})`);
              } else {
                // Fall back to selecting the account with the highest balance
                setSelectedAccount(sortedAccounts[0].account_id);
                logger.debug(`Auto-selected account with highest balance: ${sortedAccounts[0].name} ($${sortedAccounts[0].balance?.toLocaleString() || '0'})`);
              }
            }
          } else {
            logger.error('Invalid account data received:', response);
            setError('Failed to load account information. Please try again.');
          }
        } catch (err) {
          logger.error('Error fetching accounts:', err);
          setError('Failed to load accounts. Please try again.');
        } finally {
          setLoadingAccounts(false);
        }
      };
      
      fetchAccounts();
    }
  }, [show, accountId, accountTickers]);

  // Reset transaction date to today when modal is opened
  useEffect(() => {
    if (show) {
      setTransactionDate(new Date().toLocaleDateString('en-CA'));
      setCashTransactionDate(getCurrentLocalDateTime());
    }
  }, [show]);

  useEffect(() => {
    if (transactionType !== 'SELL') {
      setStopoutTriggered(false);
    }
  }, [transactionType]);

  const handleClose = () => {
    // Reset all form fields
    setTransactionType('BUY');
    setNotes('');
    setError('');
    setTickerSymbol('');
    setShares('');
    setPricePerShare('');
    setTransactionDate(new Date().toLocaleDateString('en-CA'));
    setAmount('');
    setCashType('deposit');
    setCashTransactionDate(getCurrentLocalDateTime());
    setActiveTab('security');
    setShowReview(false);
    setShowDuplicateWarning(false);
    setPriceUnavailable(false);
    setSelectedAccount(null);
    setSellAllClicked(false);
    setStopoutTriggered(false);
    onHide();
  };

  // Add function to check ticker price availability when creating ticker
  const checkTickerPrice = React.useCallback(async (symbol) => {
    if (!symbol) return false;
    
    // Check if ticker already exists in available tickers
    const existingTicker = availableTickers.find(t => 
      t.symbol.toUpperCase() === symbol.toUpperCase()
    );
    
    if (existingTicker) {
      // Check if the ticker has a current price
      if (!existingTicker.current_price) {
        setPriceUnavailable(true);
        return false;
      } else {
        setPriceUnavailable(false);
        return true;
      }
    }
    
    // For new tickers, we assume price might be unavailable
    // until we get confirmation from the server
    setPriceUnavailable(true);
    return false;
  }, [availableTickers]);

  // Check price availability when ticker symbol changes
  React.useEffect(() => {
    if (tickerSymbol) {
      checkTickerPrice(tickerSymbol);
    } else {
      setPriceUnavailable(false);
    }
  }, [tickerSymbol, checkTickerPrice]);

  // Add helper function to safely compare share amounts with a small tolerance
  const areSharesEqual = (a, b) => {
    // Use a larger epsilon value to be more permissive with floating-point precision issues
    const epsilon = 0.0001; // Increased from 0.000001
    return Math.abs(parseFloat(a) - parseFloat(b)) < epsilon;
  };

  // Add a helper function to format shares consistently
  const formatShares = (value) => {
    // Parse to float first to handle any string inputs
    const floatValue = parseFloat(value);
    if (isNaN(floatValue)) return '';
    
    // Remove trailing zeros but maintain up to 6 decimal places
    const fixedValue = floatValue.toFixed(6);
    return fixedValue.replace(/\.?0+$/, '');
  };

  // Add a function to force the exact shares value from the database
  const forceExactShares = () => {
    if (initialData && initialData.existingShares) {
      // Use the raw value directly instead of parsing/formatting
      logger.debug('FORCING EXACT DATABASE VALUE:', initialData.existingShares);
      
      // Get the raw value as a string and set it directly
      const rawValue = String(initialData.existingShares);
      setShares(rawValue);
      setSellAllClicked(true);
      return rawValue;
    }
    return shares;
  };

  // Let's add a function to get the exact account ticker information directly from the API
  const getExactTickerShares = async (accountId, tickerId) => {
    try {
      // Use the proper API service with authentication instead of fetch
      const response = await getAccountTickers(accountId);
      
      if (response && response.data) {
        const accountTickers = response.data;
        const ticker = accountTickers.find(at => at.ticker_id === tickerId);
        
        if (ticker) {
          logger.debug('Found exact ticker shares from API:', ticker.current_shares);
          // Use a tiny amount less to ensure we don't exceed due to precision issues
          const safeShares = parseFloat(ticker.current_shares) * 0.9999999;
          return safeShares;
        }
      }
      
      // Try alternative endpoint as fallback
      try {
        const altResponse = await getAggregatedTickers();
        
        if (altResponse && altResponse.data) {
          const aggregatedTickers = altResponse.data;
          // Find the ticker in the aggregated list
          for (const agg of aggregatedTickers.tickers) {
            if (agg.ticker_id === tickerId) {
              // If there are accounts with this ticker, find our specific account
              if (agg.accounts && agg.accounts.length > 0) {
                const accountInfo = agg.accounts.find(a => a.account_id === accountId);
                if (accountInfo) {
                  logger.debug('Found exact ticker shares from aggregated API:', accountInfo.shares);
                  // Use a tiny amount less to ensure we don't exceed due to precision issues
                  const safeShares = parseFloat(accountInfo.shares) * 0.9999999;
                  return safeShares;
                }
              }
            }
          }
        }
      } catch (altErr) {
        logger.error('Error fetching from alternative endpoint:', altErr);
      }
      
      // Last resort - use the shares from initialData but slightly reduced
      if (initialData && initialData.existingShares) {
        const safeShares = parseFloat(initialData.existingShares) * 0.9999999;
        logger.debug('Using safe shares from initialData:', safeShares);
        return safeShares;
      }
      
      return null;
    } catch (err) {
      logger.error('Error fetching exact ticker shares:', err);
      
      // Fallback to initialData with a safety margin if API call fails
      if (initialData && initialData.existingShares) {
        const safeShares = parseFloat(initialData.existingShares) * 0.9999999;
        logger.debug('Using safe shares from initialData after error:', safeShares);
        return safeShares;
      }
      
      return null;
    }
  };

  const handleSecurityTransactionSubmit = async () => {
    // Validation
    if (!tickerSymbol || !shares || !pricePerShare) {
      setError('Please fill in all required fields');
      return;
    }
    if (!notes.trim()) {
      setError('Please provide notes for this transaction.');
      return;
    }
    
    // Validate that shares is a valid positive number
    const sharesValue = parseFloat(shares);
    if (isNaN(sharesValue) || sharesValue <= 0) {
      setError('Please enter a valid positive number of shares');
      return;
    }
    
    // Validate selected account if accountId is null
    const effectiveAccountId = accountId || selectedAccount;
    if (!effectiveAccountId) {
      setError('Please select an account for this transaction');
      return;
    }

    // Skip the shares validation completely if sellAllClicked is true
    if (transactionType === 'SELL' && initialData && initialData.existingShares && !sellAllClicked) {
      const sharesAmount = parseFloat(shares);
      const existingShares = parseFloat(initialData.existingShares);
      
      // Only check if trying to sell more when not using sell all
      if (sharesAmount > existingShares && !areSharesEqual(sharesAmount, existingShares)) {
        const errorMsg = `Cannot sell more shares than you own (${formatShares(existingShares)})`;
        setError(errorMsg);
        return;
      }
    }
    
    setError('');
    setLoading(true);
    
    try {
      // Find ticker ID by the entered symbol
      let ticker = availableTickers.find(t => t.symbol.toUpperCase() === tickerSymbol.toUpperCase());
      
      // If ticker not found, try to create it
      if (!ticker) {
        try {
          // Create ticker without specifying a category_id
          const newTickerResponse = await createTicker({
            symbol: tickerSymbol.toUpperCase()
          });
          
          if (newTickerResponse.data) {
            ticker = newTickerResponse.data;
            // Add the newly created ticker to the available tickers
            setAvailableTickers([...availableTickers, ticker]);
            
            // Check if price is available for the new ticker
            if (!ticker.current_price) {
              setPriceUnavailable(true);
            }
          }
        } catch (tickerErr) {
          logger.error('Error creating ticker:', tickerErr);
          if (tickerErr.response && tickerErr.response.data && tickerErr.response.data.error) {
            setError(`Failed to create ticker: ${tickerErr.response.data.error}`);
          } else {
            setError('Failed to create ticker. Please try again with a valid ticker symbol.');
          }
          setLoading(false);
          return;
        }
      }
      
      // Ensure we have the account ID as a number, not string
      const numericAccountId = typeof effectiveAccountId === 'string' ? 
        parseInt(effectiveAccountId, 10) : effectiveAccountId;
        
      // Ensure we have the ticker ID as a number
      const numericTickerId = typeof ticker.ticker_id === 'string' ? 
        parseInt(ticker.ticker_id, 10) : ticker.ticker_id;
      
      // If selling all shares, try to get the exact shares amount from the backend
      let exactShares = parseFloat(shares);
      if (transactionType === 'SELL' && sellAllClicked) {
        // Get the exact shares from the backend to ensure we're using the exact value
        const backendShares = await getExactTickerShares(numericAccountId, numericTickerId);
        if (backendShares !== null) {
          exactShares = parseFloat(backendShares);
        }
      }
      
      // Format numeric values precisely for API
      // Use Number() instead of parseFloat to avoid potential precision issues  
      let sharesNum = transactionType === 'SELL' && sellAllClicked ? exactShares : Number(shares);
      
      // For sell transactions, use a slightly smaller value to ensure backend validation passes
      if (transactionType === 'SELL') {
        if (sellAllClicked) {
          // sharesNum already holds exactShares -- the live share count just
          // fetched from the backend above, with getExactTickerShares()'s own
          // 0.9999999 safety margin already applied. Re-deriving it here from
          // initialData.existingShares (the value the modal opened with,
          // potentially stale if the position changed since) would silently
          // discard that live fetch and submit a sell for the wrong amount.
        } else if (initialData && initialData.existingShares &&
            Math.abs(sharesNum - parseFloat(initialData.existingShares)) < 0.001) {
          // Not a sell-all click, but the manually-entered amount is
          // effectively the whole position -- shave it the same way so
          // backend validation (which also uses a safety margin) doesn't
          // reject it on a floating-point rounding difference.
          sharesNum = parseFloat(initialData.existingShares) * 0.9999999;
        }
      }
      
      const priceNum = Number(pricePerShare);

      // Create transaction data
      const transactionData = {
        account_id: numericAccountId,
        ticker_id: numericTickerId,
        transaction_type: transactionType,
        shares: sharesNum,
        price_per_share: priceNum,
        transaction_date: transactionDate,
        notes: notes || ""  // Ensure notes is never undefined
      };

      if (transactionType === 'SELL') {
        transactionData.is_stopout = stopoutTriggered;
      }
      
      // Create transaction with the found or created ticker ID
      const response = await createTransaction(transactionData);
      
      // Update the most recently used account cache
      updateMostRecentlyUsedAccountCache(effectiveAccountId);

      if (['BUY', 'SELL'].includes(String(transactionType).toUpperCase())) {
        markAccountTickersRefreshRequired(numericAccountId);
      }

      onTransactionComplete(response.data);
      handleClose();
    } catch (err) {
      logger.error('Error creating transaction:', err);
      
      // Log the full error details
      if (err.response) {
        logger.error('Error Status:', err.response.status);
        logger.error('Error Headers:', err.response.headers);
        logger.error('Error Data:', JSON.stringify(err.response.data));
        
        // Set a more descriptive error message
        if (err.response.data) {
          const errorMsg = err.response.data.error || JSON.stringify(err.response.data);
          setError(`API Error (${err.response.status}): ${errorMsg}`);
        } else {
          setError(`API Error (${err.response.status})`);
        }
      } else if (err.request) {
        logger.error('Error Request:', err.request);
        setError('No response received from server. Check your connection.');
      } else {
        logger.error('Error Message:', err.message);
        setError(`Error: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCashTransactionSubmit = async () => {
    try {
      const numericAmount = parseFloat(amount);
      if (isNaN(numericAmount) || numericAmount <= 0) {
        setError('Please enter a valid amount greater than 0');
        return;
      }
      if (!notes.trim()) {
        setError('Please provide notes for this transaction.');
        return;
      }

      // Validate selected account if accountId is null
      const effectiveAccountId = accountId || selectedAccount;
      if (!effectiveAccountId) {
        setError('Please select an account for this transaction');
        return;
      }

      // Make sure the account ID is a valid integer
      if (isNaN(parseInt(effectiveAccountId)) || !Number.isInteger(Number(effectiveAccountId))) {
        setError('Invalid account ID');
        logger.error('Invalid account ID:', effectiveAccountId);
        return;
      }

      if (cashType === 'withdraw' && numericAmount > currentCash) {
        setError('Insufficient funds for withdrawal');
        return;
      }

      setLoading(true);
      
      const transactionDateTime = cashTransactionDate || getCurrentLocalDateTime();

      // Log ALL data for debugging
      logger.debug('%c CASH TRANSACTION DEBUG INFO', 'background: #222; color: #bada55; font-size: 16px');
      logger.debug('Component props:', {
        accountId, 
        currentCash, 
        show,
        initialData
      });
      logger.debug('Transaction data:', {
        accountId: effectiveAccountId,
        parsedAccountId: parseInt(effectiveAccountId),
        amount: numericAmount,
        type: cashType,
        notes: notes || "",
        transactionDate: transactionDateTime
      });

      // Use the parsed integer account ID to ensure proper formatting
      const parsedAccountId = parseInt(effectiveAccountId);
      const apiUrl = `/api/accounts/${parsedAccountId}/cash`;
      
      logger.debug('API request URL:', apiUrl);
      logger.debug('API request method:', 'POST');
      logger.debug('API request headers:', { 'Content-Type': 'application/json' });
      logger.debug('API request body:', JSON.stringify({
        amount: numericAmount,
        type: cashType,
        notes: notes || "",
        transaction_date: transactionDateTime
      }));
      
      // Try a direct API call to test if endpoint exists
      try {
        const testResponse = await fetch(`/api/accounts/${parsedAccountId}`, { method: 'GET' });
        logger.debug('TEST API response status (GET account):', testResponse.status);
      } catch (testErr) {
        logger.error('TEST API call failed:', testErr);
      }
      
      // If fetch API is failing, let's try using the API service directly as an alternative method
      try {
        logger.debug('Attempting alternative API call using createTransaction service...');
        
        // Use the same API service that security transactions use
        const cashTransactionData = {
          account_id: parsedAccountId,
          transaction_type: 'CASH',
          amount: numericAmount,
          type: cashType,
          notes: notes || "",
          transaction_date: transactionDateTime
        };
        
        logger.debug('Alternative API request data:', cashTransactionData);
        
        const altResponse = await createTransaction(cashTransactionData);
        logger.debug('Alternative API call successful:', altResponse);
        
        // Update the most recently used account cache
        updateMostRecentlyUsedAccountCache(parsedAccountId);
        
        // If we get here, the alternative method worked, so we can complete the transaction
        onTransactionComplete(altResponse.data);
        handleClose();
        return; // Exit early since we succeeded
      } catch (altErr) {
        logger.error('Alternative API call failed:', altErr);
        
        // Continue with the fetch approach below
        logger.debug('Falling back to direct fetch API call...');
      }
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: numericAmount,
          type: cashType,
          notes: notes || "",
          transaction_date: transactionDateTime
        }),
      });

      logger.debug('Cash transaction response status:', response.status);
      logger.debug('Cash transaction response headers:', {
        contentType: response.headers.get('content-type'),
        statusText: response.statusText
      });
      
      let data = null;
      if (!response.ok) {
        // Get the raw text response first
        const textResponse = await response.text();
        logger.error('Server error response text:', textResponse);
        
        try {
          // Try to parse it as JSON if possible
          const jsonData = JSON.parse(textResponse);
          throw new Error(jsonData.error || 'Failed to process transaction');
        } catch (jsonError) {
          // If it's not valid JSON, provide a more descriptive error
          logger.error('Error parsing server response as JSON:', jsonError);
          throw new Error(`Server error (${response.status}): Unable to process transaction. Please try again or contact support.`);
        }
      } else {
        // For successful responses, handle the JSON normally
        data = await response.json();
        logger.debug('Cash transaction successful:', data);
      }

      // Update the most recently used account cache
      updateMostRecentlyUsedAccountCache(parsedAccountId);

      onTransactionComplete(data);
      handleClose();
    } catch (err) {
      logger.error('Cash transaction error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkForDuplicateTicker = () => {
    // Only check for BUY transactions (not needed for SELL)
    if (activeTab === 'security' && transactionType === 'BUY') {
      const upperCaseSymbol = tickerSymbol.toUpperCase();
      const existingTicker = accountTickers.find(t => 
        t.symbol.toUpperCase() === upperCaseSymbol
      );
      
      if (existingTicker) {
        setShowDuplicateWarning(true);
        return true;
      }
    }
    return false;
  };

  const handleReviewTransaction = () => {
    // Validate before showing review
    if (activeTab === 'security') {
      if (!tickerSymbol || !shares || !pricePerShare) {
        setError('Please fill in all required fields');
        return;
      }
      if (!notes.trim()) {
        setError('Please provide notes for this transaction.');
        return;
      }

      // For SELL transactions, check if trying to sell more than owned
      // with tolerance for floating-point precision
      if (transactionType === 'SELL' && initialData && initialData.existingShares) {
        const sharesAmount = parseFloat(shares);
        const existingShares = parseFloat(initialData.existingShares);
        
        // If sell all was clicked or shares are very close, allow the transaction
        if (sellAllClicked || areSharesEqual(sharesAmount, existingShares)) {
          // Use the exact raw database value to avoid any floating point issues
          forceExactShares();
        } 
        // Otherwise check if trying to sell more
        else if (sharesAmount > existingShares) {
          const errorMsg = `Cannot sell more shares than you own (${formatShares(existingShares)})`;
          setError(errorMsg);
          return;
        }
      }
      
      // Check for duplicate ticker before proceeding to review
      if (checkForDuplicateTicker()) {
        return; // Stop here and show duplicate warning
      }
    } else {
      if (!notes.trim()) {
        setError('Please provide notes for this transaction.');
        return;
      }
      const numericAmount = parseFloat(amount);
      if (isNaN(numericAmount) || numericAmount <= 0) {
        setError('Please enter a valid amount greater than 0');
        return;
      }

      if (cashType === 'withdraw' && numericAmount > currentCash) {
        setError('Insufficient funds for withdrawal');
        return;
      }
    }
    
    setError('');
    setShowReview(true);
  };

  const handleSubmit = () => {
    if (showReview) {
      // Clear any previous errors
      setError('');
      
      // Force exact shares one more time before final submission
      if (activeTab === 'security' && transactionType === 'SELL' && sellAllClicked && initialData && initialData.existingShares) {
        // Force the raw database value at the very last moment
        setShares(String(initialData.existingShares));
      }
      
      // If review is showing, proceed with actual submission
      if (activeTab === 'security') {
        handleSecurityTransactionSubmit();
      } else {
        handleCashTransactionSubmit();
      }
    } else if (showDuplicateWarning) {
      // If duplicate warning is showing and user confirms, proceed to review
      setShowDuplicateWarning(false);
      setShowReview(true);
    } else {
      // Otherwise show the review screen first
      handleReviewTransaction();
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const calculateProfit = () => {
    if (transactionType !== 'SELL' || !initialData || !initialData.ticker) {
      return null;
    }

    const currentPrice = parseFloat(pricePerShare);
    const sharesAmount = parseFloat(shares);

    if (isNaN(currentPrice) || isNaN(sharesAmount) || sharesAmount <= 0) {
      return null;
    }

    if (openLots.length > 0) {
      // LIFO: consume the most-recently-bought lot first, matching the backend's
      // actual realized-gain calculation instead of the account's average cost.
      let sharesToAllocate = sharesAmount;
      let totalCost = 0;
      let totalMatchedShares = 0;
      for (let idx = openLots.length - 1; idx >= 0 && sharesToAllocate > 0; idx -= 1) {
        const lot = openLots[idx];
        const lotShares = Number(lot.shares) || 0;
        if (lotShares <= 0) continue;
        const sharesFromLot = Math.min(lotShares, sharesToAllocate);
        totalCost += sharesFromLot * (Number(lot.cost_per_share) || 0);
        totalMatchedShares += sharesFromLot;
        sharesToAllocate -= sharesFromLot;
      }
      if (totalMatchedShares > 0) {
        const totalValue = currentPrice * totalMatchedShares;
        const profitAmount = totalValue - totalCost;
        const profitPercentage = totalCost > 0 ? (profitAmount / totalCost) * 100 : 0;
        return { amount: profitAmount, percentage: profitPercentage };
      }
    }

    // Fallback for the rare case open-lot data isn't available (e.g. a position
    // outside the trade system): average cost basis, same as before.
    const costBasis = initialData.ticker.cost_basis;
    if (!costBasis) {
      return null;
    }

    const totalCost = costBasis * sharesAmount;
    const totalValue = currentPrice * sharesAmount;
    const profitAmount = totalValue - totalCost;
    const profitPercentage = (profitAmount / totalCost) * 100;

    return {
      amount: profitAmount,
      percentage: profitPercentage
    };
  };

  const getVariant = (value) => {
    // Treat very small values (close to zero) as zero
    const absValue = Math.abs(value);
    if (absValue < 0.01) {
      return 'success'; // Show green for values close to zero
    }
    if (value > 0) return 'success';
    if (value < 0) return 'danger';
    return 'secondary';
  };

  const formatPercentage = (value) => {
    // Treat very small values (close to zero) as zero
    const absValue = Math.abs(value);
    if (absValue < 0.01) {
      return '0.00%';
    }
    return `${value.toFixed(2)}%`;
  };

  const formatReviewDate = (dateStr) => {
    if (!dateStr) {
      return '';
    }

    if (dateStr.includes('T')) {
      const date = new Date(dateStr);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleString();
      }
    }

    const parts = dateStr.split('-');
    if (parts.length >= 3) {
      const [year, month, day] = parts.map(num => parseInt(num, 10));
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString();
    }

    return dateStr;
  };

  const renderReviewContent = () => {
    let accountDisplayName = accountName;
    
    // If using selected account (not provided accountId)
    if (!accountId && selectedAccount) {
      const selectedAccountObj = accounts.find(a => a.account_id === selectedAccount);
      if (selectedAccountObj) {
        accountDisplayName = selectedAccountObj.portfolio_name ? 
          `${selectedAccountObj.name} - ${selectedAccountObj.portfolio_name}` : 
          selectedAccountObj.name;
      } else {
        accountDisplayName = 'Selected Account';
      }
    }
    
    if (activeTab === 'security') {
      // Use consistent parsing for shares value
      const sharesValue = parseFloat(shares);
      const totalValue = sharesValue * parseFloat(pricePerShare);
      const profit = transactionType === 'SELL' ? calculateProfit() : null;
      
      return (
        <div>
          <h5 className="mb-3">Review Security Transaction</h5>
          {priceUnavailable && (
            <Alert variant="warning" className="mb-3">
              <strong>Warning:</strong> Current market price for {tickerSymbol.toUpperCase()} is unavailable. 
              Transaction will be created using your entered price. The portfolio value will be calculated 
              using shares × cost basis until a market price becomes available.
            </Alert>
          )}
          
          <Table size="sm" bordered>
            <tbody>
              <tr>
                <th style={{width: '40%'}}>Account</th>
                <td>{accountDisplayName}</td>
              </tr>
              <tr>
                <th style={{width: '40%'}}>Transaction Type</th>
                <td>{transactionType === 'BUY' ? 'Buy' : 'Sell'}</td>
              </tr>
              <tr>
                <th>Ticker</th>
                <td>{tickerSymbol.toUpperCase()}</td>
              </tr>
              <tr>
                <th>Shares</th>
                <td>{formatShares(sharesValue)}</td>
              </tr>
              <tr>
                <th>Price Per Share</th>
                <td>{formatCurrency(parseFloat(pricePerShare))}</td>
              </tr>
              <tr>
                <th>Total Value</th>
                <td>{formatCurrency(totalValue)}</td>
              </tr>
              {transactionType === 'SELL' && profit && (
                <tr>
                  <th>Profit/Loss</th>
                  <td>
                    <span className={`text-${getVariant(profit.amount)}`}>
                      {formatCurrency(profit.amount)} ({profit.amount >= 0 ? '+' : ''}{formatPercentage(profit.percentage)})
                    </span>
                  </td>
                </tr>
              )}
              <tr>
                <th>Date</th>
                <td>{formatReviewDate(transactionDate)}</td>
              </tr>
              {notes && (
                <tr>
                  <th>Notes</th>
                  <td>{notes}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      );
    } else {
      return (
        <div>
          <h5 className="mb-3">Review Cash Transaction</h5>
          <Table size="sm" bordered>
            <tbody>
              <tr>
                <th style={{width: '40%'}}>Account</th>
                <td>{accountDisplayName}</td>
              </tr>
              <tr>
                <th style={{width: '40%'}}>Transaction Type</th>
                <td>{cashType === 'deposit' ? 'Deposit' : 'Withdraw'}</td>
              </tr>
              <tr>
                <th>Amount</th>
                <td>{formatCurrency(parseFloat(amount))}</td>
              </tr>
              <tr>
                <th>Date &amp; Time</th>
                <td>{formatReviewDate(cashTransactionDate)}</td>
              </tr>
              {notes && (
                <tr>
                  <th>Notes</th>
                  <td>{notes}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      );
    }
  };

  const renderDuplicateWarning = () => {
    return (
      <div>
        <Alert variant="warning">
          <Alert.Heading>Duplicate Ticker Warning</Alert.Heading>
          <p>
            This account already has a position in <strong>{tickerSymbol.toUpperCase()}</strong>. 
            Adding another transaction will result in multiple positions for the same ticker.
          </p>
          <p className="mb-0">
            Are you sure you want to continue?
          </p>
        </Alert>
      </div>
    );
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      dialogClassName="modal-custom-width"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {showReview ? 'Review Transaction' : 
           showDuplicateWarning ? 'Duplicate Ticker Warning' :
           'Add Transaction'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger">{error}</Alert>
        )}

        {showDuplicateWarning ? (
          renderDuplicateWarning()
        ) : !showReview ? (
          <>
            <Nav variant="tabs" className="mb-3" style={{ flexWrap: 'nowrap' }}>
              <Nav.Item>
                <Nav.Link 
                  active={activeTab === 'security'} 
                  onClick={() => setActiveTab('security')}
                  className="text-nowrap"
                >
                  Security Transaction
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  active={activeTab === 'cash'} 
                  onClick={() => setActiveTab('cash')}
                  className="text-nowrap"
                >
                  Cash Transaction
                </Nav.Link>
              </Nav.Item>
            </Nav>

            {/* Account selection only shown when accountId is null */}
            {!accountId && (
              <Form.Group className="mb-3" controlId="accountSelection">
                <Form.Label>Select Account</Form.Label>
                {loadingAccounts ? (
                  <div className="text-center">
                    <Spinner animation="border" size="sm" /> Loading accounts...
                  </div>
                ) : accounts.length === 0 ? (
                  <Alert variant="warning">
                    No accounts available. Please create an account first.
                  </Alert>
                ) : (
                  <Form.Select
                    value={selectedAccount || ''}
                    onChange={(e) => setSelectedAccount(e.target.value)}
                    required
                  >
                    {accounts.map(account => (
                      <option key={account.account_id} value={account.account_id}>
                        {account.name} - {account.portfolio_name || 'Unknown Portfolio'}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>
            )}
            
            {/* Display account when accountId is provided */}
            {accountId && (
              <Form.Group className="mb-3">
                <Form.Label>Account</Form.Label>
                <Form.Control
                  type="text"
                  value={accountName}
                  disabled
                  className="bg-light"
                />
              </Form.Group>
            )}

            {activeTab === 'security' ? (
              <Form>
                <div className="row">
                  <div className="col-7">
                    <Form.Group className="mb-3" controlId="tickerSymbol">
                      <Form.Label>Ticker Symbol</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g., AAPL"
                        value={tickerSymbol}
                        onChange={(e) => setTickerSymbol(e.target.value.toUpperCase())}
                        required
                      />
                      <Form.Text className="text-muted">
                        Enter a valid ticker symbol.
                      </Form.Text>
                    </Form.Group>
                  </div>
                  <div className="col-5">
                    <Form.Group className="mb-3" controlId="transactionType">
                      <Form.Label>Transaction Type</Form.Label>
                      <Form.Select
                        value={transactionType}
                        onChange={(e) => setTransactionType(e.target.value)}
                        required
                      >
                        <option value="BUY">Buy</option>
                        <option value="SELL">Sell</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                </div>
                
                {priceUnavailable && tickerSymbol && (
                  <Alert variant="warning" className="mb-3 py-2">
                    <small>
                      <strong>Note:</strong> Current market price for {tickerSymbol.toUpperCase()} is unavailable. 
                      You can still create the transaction, and portfolio value will use your entered price 
                      until market data becomes available.
                    </small>
                  </Alert>
                )}
                
                {transactionType === 'SELL' && initialData && initialData.ticker && initialData.ticker.cost_basis && (
                  <Alert variant={calculateProfit()?.amount >= 0 ? 'success' : 'danger'} className="mb-3 py-2">
                    <small>
                      <strong>Selling position:</strong> {' '}
                      {calculateProfit() ? (
                        <>
                          {calculateProfit().amount >= 0 ? 'Profit' : 'Loss'}: {formatCurrency(calculateProfit().amount)} ({calculateProfit().amount >= 0 ? '+' : ''}{formatPercentage(calculateProfit().percentage)})
                        </>
                      ) : 'Unable to calculate profit/loss'}
                    </small>
                  </Alert>
                )}
                
                <div className="row">
                  <div className="col">
                    <Form.Group className="mb-3" controlId="shares">
                      <Form.Label>Shares</Form.Label>
                      <div className="d-flex">
                        <Form.Control
                          type="number"
                          step="0.000001"
                          min="0.000001"
                          placeholder="Enter shares"
                          value={shares}
                          onChange={(e) => {
                            // When selling, if value is close to max shares, auto-adjust to max
                            if (transactionType === 'SELL' && initialData && initialData.existingShares) {
                              const inputValue = parseFloat(e.target.value);
                              const existingShares = parseFloat(initialData.existingShares);
                              
                              // If very close to max or slightly over max, set to exact max
                              if (Math.abs(inputValue - existingShares) < 0.001 || 
                                  (inputValue > existingShares && inputValue < existingShares * 1.001)) {
                                setSellAllClicked(true);
                                setShares(initialData.existingShares.toString());
                                return;
                              }
                            }
                            
                            setShares(e.target.value);
                          }}
                          required
                        />
                        {transactionType === 'SELL' && initialData && initialData.existingShares && (
                          <Button 
                            variant="outline-secondary" 
                            size="sm" 
                            className="ms-2"
                            onClick={() => {
                              forceExactShares();
                            }}
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            Sell All
                          </Button>
                        )}
                      </div>
                      {transactionType === 'SELL' && initialData && initialData.existingShares && (
                        <Form.Text className="text-muted">
                          Available: {formatShares(initialData.existingShares)} shares
                        </Form.Text>
                      )}
                    </Form.Group>
                  </div>
                  <div className="col">
                    <Form.Group className="mb-3" controlId="pricePerShare">
                      <Form.Label>Price Per Share</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.0001"
                        min="0.0001"
                        placeholder="Enter price"
                        value={pricePerShare}
                        onChange={(e) => setPricePerShare(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col">
                    <Form.Group className="mb-3" controlId="transactionDate">
                      <Form.Label>Transaction Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={transactionDate}
                        onChange={(e) => {
                          // Save the date string directly from the input without timezone conversion
                          setTransactionDate(e.target.value);
                        }}
                        required
                      />
                    </Form.Group>
                  </div>
                </div>
                
                <Form.Group className="mb-3" controlId="notes">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder=""
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    required
                  />
                  <div className="d-flex gap-2 flex-wrap mt-2 mb-2">
                    {transactionType === 'BUY' ? (
                      <>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Breaking out of base')}
                        >
                          Breaking out of base
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Breaking out of consolidation')}
                        >
                          Breaking out of consolidation
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Breaking downtrend')}
                        >
                          Breaking downtrend
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Uptrending')}
                        >
                          Uptrending
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Pull back at 21 day')}
                        >
                          Pull back at 21 day
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Pull back at 50 day')}
                        >
                          Pull back at 50 day
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Pending Breakout')}
                        >
                          Pending Breakout
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Early Entry')}
                        >
                          Early Entry
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Add on profit')}
                        >
                          Add on profit
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Base Play')}
                        >
                          Base Play
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Volatile Play')}
                        >
                          Volatile Play
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Strength Play')}
                        >
                          Strength Play
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Tennis Ball Action')}
                        >
                          Tennis Ball Action
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Free up capital')}
                        >
                          Free up capital
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Hit stop loss')}
                        >
                          Hit stop loss
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Lock in profits')}
                        >
                          Lock in profits
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Reduce Risks')}
                        >
                          Reduce Risks
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleNotesPresetClick('Egg Splat Action')}
                        >
                          Egg Splat Action
                        </Button>
                      </>
                    )}
                  </div>
                  {transactionType === 'SELL' && (
                    <Form.Check
                      type="switch"
                      id="stopout-flag"
                      label="Stop loss hit (mark as stopout)"
                      className="mt-1"
                      checked={stopoutTriggered}
                      onChange={(e) => setStopoutTriggered(e.target.checked)}
                    />
                  )}
                  <Form.Text className="text-muted">State the reasons for this transaction.</Form.Text>
                </Form.Group>
              </Form>
            ) : (
              <Form>
                <div className="row">
                  <div className="col-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Transaction Type</Form.Label>
                      <Form.Select
                        value={cashType}
                        onChange={(e) => setCashType(e.target.value)}
                      >
                        <option value="deposit">Deposit</option>
                        <option value="withdraw">Withdraw</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <div className="col-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Amount</Form.Label>
                      <Form.Control
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="Enter amount"
                        min="0"
                        step="0.01"
                        required
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <Form.Group className="mb-3">
                      <Form.Label>Transaction Date & Time</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={cashTransactionDate}
                        onChange={(e) => setCashTransactionDate(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </div>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder=""
                    required
                  />
                  <div className="d-flex gap-2 flex-wrap mt-2 mb-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleNotesPresetClick('Breaking out of base')}
                    >
                      Breaking out of base
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleNotesPresetClick('Breaking out of consolidation')}
                    >
                      Breaking out of consolidation
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleNotesPresetClick('Breaking downtrend')}
                    >
                      Breaking downtrend
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleNotesPresetClick('Uptrending')}
                    >
                      Uptrending
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleNotesPresetClick('Pull back at 21 day')}
                    >
                      Pull back at 21 day
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleNotesPresetClick('Pull back at 50 day')}
                    >
                      Pull back at 50 day
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleNotesPresetClick('Pending Breakout')}
                    >
                      Pending Breakout
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleNotesPresetClick('Add on profit')}
                    >
                      Add on profit
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleNotesPresetClick('Base Play')}
                    >
                      Base Play
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleNotesPresetClick('Volatile Play')}
                    >
                      Volatile Play
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleNotesPresetClick('Strength Play')}
                    >
                      Strength Play
                    </Button>
                  </div>
                  <Form.Text className="text-muted">State the reasons for this transaction.</Form.Text>
                </Form.Group>

                {cashType === 'withdraw' && (
                  <div className="text-muted mb-3">
                    Available Cash: ${currentCash.toFixed(2)}
                  </div>
                )}
              </Form>
            )}
          </>
        ) : (
          // Review content
          renderReviewContent()
        )}
      </Modal.Body>
      <Modal.Footer>
        {showDuplicateWarning ? (
          <>
            <Button variant="secondary" onClick={() => setShowDuplicateWarning(false)}>
              Cancel
            </Button>
            <Button 
              variant="warning" 
              onClick={handleSubmit}
            >
              Continue Anyway
            </Button>
          </>
        ) : showReview ? (
          <>
            <Button variant="secondary" onClick={() => setShowReview(false)}>
              Back
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Confirm & Save'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSubmit}
            >
              Review Transaction
            </Button>
          </>
        )}
      </Modal.Footer>

      <style>
        {`
          .modal-custom-width {
            max-width: 550px;
            margin: 1.75rem auto;
          }
        `}
      </style>
    </Modal>
  );
};

export default TransactionModal; 
